/** @jsxImportSource @emotion/react */
import { useState } from "react";
import { Card } from "primereact/card";
import { Accordion, AccordionTab } from "primereact/accordion";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { TabView, TabPanel } from "primereact/tabview";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import Boton from "../boton";
import Titulo from "../titulo";
import ArchivoPreview from "../archivoPreview";
import { downloadFile, formatFecha, useIsMobile } from "../../utils";

// Vista de lectura del cumplimiento de un organismo con su norma de
// referencia: resumen por estado, filtro, un acordeón por apartado y una fila
// por requisito. La usan el admin (que edita) y el auditor SG (solo lectura).

export const ESTADOS_CUMPLIMIENTO = {
  PENDIENTE: { etiqueta: "Sin declarar", severidad: "secondary" },
  PENDIENTE_REVISION: { etiqueta: "Pendiente", severidad: "contrast" },
  CUMPLE: { etiqueta: "Cumple", severidad: "success" },
  PARCIAL: { etiqueta: "Parcial", severidad: "warning" },
  NO_CUMPLE: { etiqueta: "No cumple", severidad: "danger" },
  NO_APLICA: {
    etiqueta: "No aplica",
    severidad: "secondary",
    // Gris oscuro para no confundirse con "Sin declarar", que usa el gris
    // claro de la severidad secondary.
    estilo: { background: "#64748b", color: "white" },
  },
};

// Sin fila de cumplimiento, el requisito está sin declarar.
export const estadoDe = (requisito) =>
  requisito.cumplimiento?.estado || "PENDIENTE";

// Documentos vinculados: los confirmados y los "a revisar" (confirmados cuya
// versión nueva el asistente cree que ya no lo cubre; siguen vinculados hasta
// que alguien decida).
export const vinculadas = (requisito) =>
  requisito.evidencias.filter((e) =>
    ["CONFIRMADA", "REVISAR"].includes(e.estado),
  );

// Las sugerencias del asistente todavía no respaldan nada: se confirman
// agregando el documento a "Documentos que lo respaldan".
export const sugerencias = (requisito) =>
  requisito.evidencias.filter((e) => e.estado === "PROPUESTA");

export const etiquetaDocumento = (d, version) =>
  `${d.codigo} - ${d.nombre} (v${version})`;

/**
 * Documento del sistema citado como evidencia: el texto abre la vista previa
 * de la versión evaluada. Si la versión no tiene archivo, queda como texto.
 */
export function DocumentoEvidencia({ evidencia, etiqueta }) {
  return (
    <ArchivoPreview
      nombre={evidencia.archivo?.nombredescarga}
      hash={evidencia.archivo?.hash}
      etiqueta={etiqueta}
    />
  );
}

// Lista de documentos separados por coma, cada uno con su vista previa.
function ListaDocumentos({ evidencias, detalle }) {
  return evidencias.map((e, i) => (
    <span key={e.id}>
      {i > 0 && ", "}
      <DocumentoEvidencia
        evidencia={e}
        etiqueta={`${e.documento.codigo} (v${e.version}${detalle?.(e) || ""})`}
      />
    </span>
  ));
}

// Valor del desplegable de opción para "sin elegir": Dropdown no distingue
// bien un null de "todavía no elegí".
const SIN_OPCION = "SIN_OPCION";

/**
 * Archivo que demuestra el cumplimiento: el nombre abre la vista previa y el
 * botón lo descarga. `rutaDescarga` es el endpoint del rol que lo muestra.
 */
export function ArchivoCumplimiento({ archivo, toast, rutaDescarga }) {
  const [descargando, setDescargando] = useState(false);

  function descargar() {
    setDescargando(true);
    downloadFile(
      rutaDescarga(archivo),
      archivo.nombredescarga,
      () => setDescargando(false),
      (respuesta) => {
        setDescargando(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.[0]?.msg || "No se pudo descargar el archivo",
          sticky: true,
        });
      },
    );
  }

  return (
    <span
      css={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}
    >
      <ArchivoPreview nombre={archivo.nombredescarga} hash={archivo.hash} />
      <Boton texto icono chico negro callback={descargar} loader={descargando}>
        <i className="pi pi-download" />
      </Boton>
    </span>
  );
}

/**
 * @param {Object} props.datos - Respuesta del endpoint de cumplimiento:
 *   { normas, opcionSistemaGestion }.
 * @param {Object} props.toast - Ref del Toast de la pantalla.
 * @param {Function} props.rutaDescargaArchivo - (archivo) => endpoint para
 *   descargar el archivo de un requisito.
 * @param {Function} [props.onEditar] - (requisito) => void. Sin esto no se
 *   muestra el lápiz: la vista queda de solo lectura.
 * @param {Function} [props.onCambiarOpcion] - (opcion) => void. Sin esto no se
 *   muestra el selector de opción A/B.
 * @param {Function} [props.accionesNorma] - (norma) => nodo que va junto a los
 *   filtros de cada norma cuando hay más de una.
 * @param {Function} [props.botonesTitulo] - (norma | null) => nodo para el
 *   título: la norma si hay una sola, null si hay varias (con pestañas).
 * @param {string} [props.urlVolver] - Botón de volver en el título.
 */
export default function VistaCumplimiento({
  datos,
  toast,
  rutaDescargaArchivo,
  onEditar,
  onCambiarOpcion,
  accionesNorma,
  botonesTitulo,
  urlVolver,
}) {
  const [filtroEstado, setFiltroEstado] = useState(null);
  const esMobile = useIsMobile();

  if (datos.normas.length === 0) {
    return (
      <>
        <Titulo urlVolver={urlVolver}>Cumplimiento</Titulo>
        <Message
          severity="info"
          css={{ width: "100%", justifyContent: "flex-start" }}
          text="El organismo no tiene una norma de referencia asignada."
        />
      </>
    );
  }

  // Los apartados de la opción que no se eligió no cuentan ni se muestran.
  const apartadosVisibles = (norma) =>
    norma.apartados.filter(
      (a) =>
        !a.opcion ||
        !datos.opcionSistemaGestion ||
        a.opcion === datos.opcionSistemaGestion,
    );

  const ofreceOpciones = (norma) => norma.apartados.some((a) => a.opcion);

  const vistaNorma = (norma) => {
    const apartados = apartadosVisibles(norma);
    const requisitos = apartados.flatMap((a) => a.requisitos);
    const conteo = Object.fromEntries(
      Object.keys(ESTADOS_CUMPLIMIENTO).map((estado) => [
        estado,
        requisitos.filter((r) => estadoDe(r) === estado).length,
      ]),
    );

    return (
      <>
        <div
          css={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "1rem",
            marginBottom: "1rem",
          }}
        >
          <div css={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {Object.entries(ESTADOS_CUMPLIMIENTO).map(([estado, e]) => (
              <Tag
                key={estado}
                value={`${e.etiqueta}: ${conteo[estado]}`}
                severity={e.severidad}
                style={e.estilo}
              />
            ))}
          </div>

          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginLeft: "auto",
            }}
          >
            {datos.normas.length > 1 && accionesNorma?.(norma)}
            {onCambiarOpcion && ofreceOpciones(norma) && (
              <Dropdown
                value={datos.opcionSistemaGestion || SIN_OPCION}
                options={[
                  {
                    value: SIN_OPCION,
                    label: "Sistema de gestión: sin elegir",
                  },
                  { value: "A", label: "Sistema de gestión: opción A" },
                  { value: "B", label: "Sistema de gestión: opción B" },
                ]}
                onChange={(e) =>
                  onCambiarOpcion(e.value === SIN_OPCION ? null : e.value)
                }
              />
            )}
            <Dropdown
              value={filtroEstado}
              options={Object.entries(ESTADOS_CUMPLIMIENTO).map(
                ([value, e]) => ({
                  value,
                  label: e.etiqueta,
                }),
              )}
              onChange={(e) => setFiltroEstado(e.value)}
              placeholder="Todos los estados"
              showClear
            />
          </div>
        </div>

        <Accordion
          multiple
          transitionOptions={{ timeout: 0 }}
          css={{
            "& .p-accordion-header-link": { color: "black" },
            "& .p-toggleable-content": { transition: "none !important" },
          }}
        >
          {apartados
            .map((apartado) => ({
              apartado,
              filtrados: apartado.requisitos.filter(
                (r) => !filtroEstado || estadoDe(r) === filtroEstado,
              ),
            }))
            .filter(({ filtrados }) => filtrados.length > 0)
            .map(({ apartado, filtrados }) => {
              const cumplen = apartado.requisitos.filter((r) =>
                ["CUMPLE", "NO_APLICA"].includes(estadoDe(r)),
              ).length;

              return (
                <AccordionTab
                  key={apartado.id}
                  header={
                    <div
                      css={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "1rem",
                        width: "100%",
                      }}
                    >
                      <span>
                        {apartado.numero} {apartado.titulo}
                      </span>
                      <Tag
                        value={`${cumplen}/${apartado.requisitos.length}`}
                        severity={
                          cumplen === apartado.requisitos.length
                            ? "success"
                            : "warning"
                        }
                      />
                    </div>
                  }
                >
                  <DataTable value={filtrados}>
                    <Column
                      header="Número"
                      body={(r) => (
                        <span css={{ fontSize: "1.15em" }}>{r.numero}</span>
                      )}
                      style={{ width: "130px", verticalAlign: "middle" }}
                    />
                    {!esMobile && (
                      <Column
                        header="Requisito"
                        body={(r) => (
                          <>
                            <div>{r.texto}</div>
                            {r.plataforma && (
                              <div
                                css={{
                                  marginTop: "0.5rem",
                                  color: "var(--grisTextos)",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                <strong>Plataforma:</strong> {r.plataforma}
                              </div>
                            )}
                            {vinculadas(r).length > 0 && (
                              <div css={{ marginTop: "0.5rem" }}>
                                <strong>Documentos:</strong>{" "}
                                <ListaDocumentos
                                  evidencias={vinculadas(r)}
                                  detalle={(e) =>
                                    e.estado === "REVISAR" ? ", a revisar" : ""
                                  }
                                />
                              </div>
                            )}
                            {sugerencias(r).length > 0 && (
                              <div css={{ marginTop: "0.5rem" }}>
                                <strong>Sugerencias:</strong>{" "}
                                <ListaDocumentos evidencias={sugerencias(r)} />
                              </div>
                            )}
                            {r.cumplimiento?.comentario && (
                              <div
                                css={{
                                  marginTop: "0.5rem",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                <strong>Notas:</strong>{" "}
                                {r.cumplimiento.comentario}
                              </div>
                            )}
                            {r.cumplimiento?.archivo && (
                              <div css={{ marginTop: "0.5rem" }}>
                                <strong>Archivo:</strong>{" "}
                                <ArchivoCumplimiento
                                  archivo={r.cumplimiento.archivo}
                                  toast={toast}
                                  rutaDescarga={rutaDescargaArchivo}
                                />
                              </div>
                            )}
                          </>
                        )}
                      />
                    )}
                    <Column
                      header="Estado"
                      body={(r) => (
                        <>
                          <Tag
                            value={ESTADOS_CUMPLIMIENTO[estadoDe(r)].etiqueta}
                            severity={ESTADOS_CUMPLIMIENTO[estadoDe(r)].severidad}
                            style={ESTADOS_CUMPLIMIENTO[estadoDe(r)].estilo}
                          />
                          {r.cumplimiento && (
                            <div
                              css={{
                                marginTop: "0.25rem",
                                fontSize: "0.8em",
                                color: "var(--grisTextos)",
                              }}
                            >
                              {r.cumplimiento.usuario}
                              <br />
                              {formatFecha(r.cumplimiento.fechaActualizacion)}
                            </div>
                          )}
                        </>
                      )}
                      style={{ width: "150px", verticalAlign: "top" }}
                    />
                    {onEditar && (
                      <Column
                        header=""
                        body={(r) => (
                          <Boton
                            texto
                            icono
                            chico
                            negro
                            callback={() => onEditar(r)}
                          >
                            <i className="pi pi-pencil" />
                          </Boton>
                        )}
                        style={{
                          width: "60px",
                          textAlign: "center",
                          verticalAlign: "top",
                        }}
                      />
                    )}
                  </DataTable>
                </AccordionTab>
              );
            })}
        </Accordion>
      </>
    );
  };

  if (datos.normas.length === 1) {
    return (
      <>
        <Titulo
          urlVolver={urlVolver}
          botones={botonesTitulo?.(datos.normas[0])}
        >
          Cumplimiento - {datos.normas[0].codigo}
        </Titulo>
        <Card>{vistaNorma(datos.normas[0])}</Card>
      </>
    );
  }

  return (
    <>
      <Titulo urlVolver={urlVolver} botones={botonesTitulo?.(null)}>
        Cumplimiento
      </Titulo>
      <Card>
        {/* Las normas quedan montadas y solo se ocultan: al cambiar de pestaña
            no se desmonta una lista y se monta la otra, que por un instante
            dejaba la página sin scroll y el contenido se sacudía. También
            conserva los apartados abiertos de cada norma. */}
        <TabView renderActiveOnly={false}>
          {datos.normas.map((norma) => (
            <TabPanel key={norma.id} header={norma.codigo}>
              {vistaNorma(norma)}
            </TabPanel>
          ))}
        </TabView>
      </Card>
    </>
  );
}
